import { readAccessDeviceRegistry } from '@/api/core/access-device-registry';
import { canReadProperties, coreQueryKey } from '@/api/core/authorization';
import { isApiError } from '@/api/http/errors';
import { Button } from '@mgrvtechgroup/admin-ds';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { PropertyRuntime } from '../property/property-queries';

/** 규칙에서 활성 Clue 기기를 실제 원장 목록에서 선택한다. */
export function ClueDevicePicker({
  runtime,
  generation,
  onSelect,
}: {
  runtime: PropertyRuntime;
  generation: number;
  onSelect: (id: string) => void;
}) {
  const [cursor, setCursor] = useState<string>();
  const allowed = () => {
    const current = runtime.session.getSnapshot();
    return (
      current.status === 'authorized' &&
      current.generation === generation &&
      !!current.authorization &&
      !current.deniedDomains.includes('property') &&
      canReadProperties(current.authorization)
    );
  };
  const devices = useQuery({
    queryKey: [
      ...coreQueryKey(runtime.apiNamespace, generation, 'access-device-picker'),
      cursor ?? '',
    ],
    enabled: allowed(),
    queryFn: async ({ signal }) => {
      if (!allowed()) throw new Error('기기 조회 권한이 없습니다.');
      try {
        const result = await readAccessDeviceRegistry(runtime.client, { cursor }, signal);
        if (!allowed()) throw new Error('기기 조회 권한이 없습니다.');
        return result;
      } catch (error) {
        if (isApiError(error) && error.status === 403 && !signal.aborted && allowed())
          runtime.session.reportDomainAccessDenied('property', generation);
        throw error;
      }
    },
  });
  return (
    <section aria-label="Clue 기기 선택">
      {devices.isPending && allowed() ? <p>기기를 조회하고 있습니다.</p> : null}
      {devices.isError || !allowed() ? (
        <p role="alert">기기 목록을 조회할 수 없습니다. 기기 UUID를 직접 입력할 수 있습니다.</p>
      ) : null}
      {devices.data && allowed() ? (
        <>
          <ul>
            {devices.data.items
              .filter(
                (item) => item.provider === 'CLUE' && !item.retired && item.is_active,
              )
              .map((item) => (
                <li key={item.core_device_id}>
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (allowed()) onSelect(item.core_device_id);
                    }}
                  >
                    {item.device_name ?? item.device_id} · {item.device_id} ·{' '}
                    {item.registration_property_id ?? '지점 미확인'}
                  </Button>
                </li>
              ))}
          </ul>
          {devices.data.next_cursor ? (
            <Button onClick={() => { setCursor(devices.data.next_cursor ?? undefined); }}>
              다음 기기
            </Button>
          ) : null}
          {cursor ? (
            <Button variant="outline" onClick={() => { setCursor(undefined); }}>
              처음 기기
            </Button>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
